import { createContext, useContext, type ReactNode } from 'react'

import { cn } from '@/components/base'
import { GroupHeading } from '@/components/Internal/GroupHeading'

/**
 * The frame itself, for a caller that needs the box without the grammar — a
 * table fills its own frame with a toolbar and rows, and has no heading of the
 * kind a panel writes.
 */
export const PANEL_FRAME_CLASS =
  'overflow-hidden rounded-lg border border-border bg-card'

/**
 * The two densities a panel comes in.
 *
 * `body` is the inset head, sections and foot share, so a label and the value
 * under it start on the same line. `firstTight` puts the first section closer
 * to a heading than two sections sit to each other.
 */
const SIZES = {
  sm: {
    body: 'px-4 py-3',
    foot: 'px-4 py-2',
    firstTight: '[&>*:first-child]:pt-2',
  },
  md: {
    body: 'px-5 py-4',
    foot: 'px-5 py-2.5',
    firstTight: '[&>*:first-child]:pt-2.5',
  },
} as const

export type PanelSize = keyof typeof SIZES

/** The panel's own size, so a section cannot be padded differently from the
 *  box around it. */
const PanelSizeContext = createContext<PanelSize>('md')

export const usePanelPadding = () => SIZES[useContext(PanelSizeContext)].body

/**
 * A group of content in a frame.
 *
 * The same thing `SheetGroup` is — a heading, a sentence, a body — with a
 * hairline around it, for content that is an object and not a section of the
 * page: something to be deleted, sorted, or read beside its siblings.
 * Both read their heading from `GroupHeading`, so the framed and the flat
 * grammar cannot say a title two ways.
 *
 * The frame is the same hairline a table row is separated by, and it carries
 * no tint: a panel is quiet on white and on the grey ground alike.
 */
export function Panel({
  title,
  subtitle,
  badge,
  action,
  footer,
  size = 'md',
  reserveAction = false,
  className,
  children,
}: {
  title?: string
  subtitle?: string
  badge?: ReactNode
  action?: ReactNode
  /** A quiet line under the body: a condition, a tally, a consequence. */
  footer?: ReactNode
  /** `sm` is the denser panel the detail modals and log panes use. */
  size?: PanelSize
  /** Passed to the head: see `GroupHeading`. */
  reserveAction?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <PanelSizeContext.Provider value={size}>
      <div className={cn('flex flex-col', PANEL_FRAME_CLASS, className)}>
        {title ? (
          <div className={cn(SIZES[size].body, 'pb-0')}>
            <GroupHeading
              reserveAction={reserveAction}
              title={title}
              subtitle={subtitle}
              badge={badge}
              action={action}
            />
          </div>
        ) : null}

        <div
          className={cn(
            'flex min-w-0 flex-col divide-y divide-line-soft',
            title && SIZES[size].firstTight,
          )}
        >
          {children}
        </div>

        {footer ? (
          <div
            className={cn(
              'flex flex-row flex-wrap items-center gap-2 border-t border-line-soft text-xs text-faint',
              SIZES[size].foot,
            )}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </PanelSizeContext.Provider>
  )
}
